import {createReducer} from '@reduxjs/toolkit'
import {AnyAction} from 'redux'
import {
  clearCheckout,
  editStep,
  nextFormClicked,
  nextStepAction,
  setBuyerDetails,
  setExpandedTicketIndex,
  setTicketDetails as setTicketDetailsAction,
  setUseBuyerDetails,
  setValidPaymentAdded,
} from '../actions/checkout'
import {RESERVE_TICKETS_OLD, makeReservation} from '../actions/reservation'
import {CHECKOUT_STEPS} from '../constants/constants'
import {CheckoutState} from '../types/state'

export const defaultCheckoutState: CheckoutState = {
  useBuyerDetails: false,
  currentStep: CHECKOUT_STEPS[0],
  validPaymentAdded: false,
  buyerDetails: null,
  ticketsDetails: [],
  ticketsDetailsValidity: [],
  expandedTicketIndex: 0,
}

export const checkout = createReducer(defaultCheckoutState, builder => {
  builder
    .addCase(setBuyerDetails, (state, action) => {
      const buyerDetails = action.payload

      if (state.useBuyerDetails) {
        const ticketsDetails = [...state.ticketsDetails]

        ticketsDetails[0] = {
          ...ticketsDetails[0],
          ...buyerDetails,
        }
        state.buyerDetails = buyerDetails
        state.ticketsDetails = ticketsDetails
      } else {
        state.buyerDetails = buyerDetails
      }
    })
    .addCase(setTicketDetailsAction, (state, action) => {
      return setTicketDetails(state, action)
    })
    .addCase(setValidPaymentAdded, (state, action) => {
      state.validPaymentAdded = action.payload
    })
    .addCase(setUseBuyerDetails, (state, action) => {
      return handleUseBuyerDetails(state, action)
    })
    .addCase(nextStepAction, (state, action) => {
      state.currentStep = action.payload
    })
    .addCase(editStep, (state, action) => {
      const {step, validPaymentAdded} = action.payload
      state.currentStep = step
      state.validPaymentAdded = validPaymentAdded
    })
    .addCase(nextFormClicked, state => {
      state.expandedTicketIndex = state.expandedTicketIndex + 1
    })
    .addCase(setExpandedTicketIndex, (state, action) => {
      state.expandedTicketIndex = action.payload
    })
    .addCase(RESERVE_TICKETS_OLD.SUCCESS, (state, action: AnyAction) => {
      const ticketsDetailsValidity = action.payload.reservations.reduce(
        (acc, ticketReservation) => [...acc, ...Array(ticketReservation.quantity).fill(false)],
        [],
      )
      state.ticketsDetailsValidity = ticketsDetailsValidity
    })
    .addCase(makeReservation.fulfilled, (state, action) => {
      const reservations = action.payload.reservations || []
      const ticketsDetailsValidity = reservations.reduce(
        (acc, ticketReservation) => [...acc, ...Array(ticketReservation.quantity || 0).fill(false)],
        [],
      )

      state.ticketsDetailsValidity = ticketsDetailsValidity
    })
    .addCase(clearCheckout, () => {
      return defaultCheckoutState
    })
})

const handleUseBuyerDetails = (state: CheckoutState, action: ReturnType<typeof setUseBuyerDetails>) => {
  if (setUseBuyerDetails.match(action)) {
    const {
      buyerDetails: {firstName, lastName, email},
    } = state
    const {useBuyerDetails, ticketIndex = 0} = action.payload

    const shouldUseBuyerDetails = useBuyerDetails
    const buyerDetails = shouldUseBuyerDetails
      ? {firstName, lastName, email}
      : {firstName: null, lastName: null, email: null}
    const ticketsDetails = [...state.ticketsDetails]

    ticketsDetails[ticketIndex] = {
      ...ticketsDetails[ticketIndex],
      ...buyerDetails,
    }

    return {
      ...state,
      ticketsDetails,
      useBuyerDetails: shouldUseBuyerDetails,
    }
  }
}

const setTicketDetails = (state: CheckoutState, action: ReturnType<typeof setTicketDetailsAction>) => {
  const ticketsDetails = [...state.ticketsDetails]
  const ticketsDetailsValidity = [...state.ticketsDetailsValidity]

  ticketsDetails[action.payload.index] = action.payload.details
  ticketsDetailsValidity[action.payload.index] = action.payload.valid

  return {
    ...state,
    ticketsDetails,
    ticketsDetailsValidity,
  }
}
